/* Shared slider controls — side-arrow forwarding + segmented pagination.
   Used by:
     - Featured Collection  (.featured-collection--new-nav)
     - Ingredients Slider   (.bs-ingredients-slider)
   Behaviour per section is driven by SECTION_CONFIGS below. */

(function () {
  const INIT_FLAG = 'sliderControlsInited';

  const SECTION_CONFIGS = [
    {
      rootSelector: '.featured-collection--new-nav',
      progressSelector: '.fc-slider-controls__progress',
      progressContainerSelector: '.fc-slider-controls',
      segmentClass: 'fc-slider-controls__progress-segment',
      prevBtnSelector: '.fc-slider-controls__btn[name="prev"]',
      nextBtnSelector: '.fc-slider-controls__btn[name="next"]',
      // Featured collection has hidden nav buttons in .hometitle that the
      // carousel-slider component wires up — we forward clicks from the
      // visible round side arrows to those hidden buttons.
      wireSideArrows: true,
      originalPrevSelector: '.hometitle .slider-nav__btn[name="prev"]',
      originalNextSelector: '.hometitle .slider-nav__btn[name="next"]',
      // 'per-page' → one segment per (perPage-sized) page.
      // 'per-slide' → one segment per slide.
      paginationMode: 'per-page',
    },
    {
      rootSelector: '.bs-ingredients-slider',
      progressSelector: '.bs-is-slider-controls__progress',
      progressContainerSelector: '.bs-is-slider-controls',
      segmentClass: 'bs-is-slider-controls__progress-segment',
      prevBtnSelector: '.bs-is-slider-controls__btn[name="prev"]',
      nextBtnSelector: '.bs-is-slider-controls__btn[name="next"]',
      // Round arrows live directly inside .slider-nav, so carousel-slider
      // wires them itself — nothing to forward.
      wireSideArrows: false,
      paginationMode: 'per-slide',
      // Pulse the clicked slide's card briefly on pagination click.
      highlightOnClick: {
        selector: '.bs-ingredients-slider__card',
        className: 'is-highlighted',
      },
    },
  ];

  function forwardClick(target) {
    return (event) => {
      event.preventDefault();
      if (target) target.click();
    };
  }

  function wireSideArrows(state) {
    if (!state.config.wireSideArrows) return;
    const origPrev = state.root.querySelector(state.config.originalPrevSelector);
    const origNext = state.root.querySelector(state.config.originalNextSelector);
    if (state.prevBtn && origPrev) state.prevBtn.addEventListener('click', forwardClick(origPrev));
    if (state.nextBtn && origNext) state.nextBtn.addEventListener('click', forwardClick(origNext));
  }

  function segmentCount(state) {
    if (state.config.paginationMode === 'per-slide') return state.slides.length;
    return state.totalPages;
  }

  function trackOverflows(state) {
    if (state.slides.length < 2) return false;
    const viewport = state.slider.querySelector('.slider');
    if (!viewport) return state.slides.length > state.perPage;
    if (viewport.clientWidth === 0) return state.slides.length > 1;
    // Don't use data-per-page: it's a desktop column count (~1.5 cards on mobile).
    return viewport.scrollWidth - viewport.clientWidth > 1;
  }

  function updatePaginationVisibility(state) {
    if (!state.progressContainerEl) return;
    // Hide when nothing to page through: the track fits, or per-page mode
    // only produced one segment (desktop perPage vs mobile overflow).
    state.progressContainerEl.hidden = !trackOverflows(state) || segmentCount(state) < 2;
  }

  function buildSegments(state) {
    state.progressEl.innerHTML = '';
    state.segments = [];
    const count = segmentCount(state);
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i += 1) {
      const seg = document.createElement('button');
      seg.type = 'button';
      seg.className = state.config.segmentClass;
      const label = state.config.paginationMode === 'per-slide' ? `Slide ${i + 1}` : `Page ${i + 1}`;
      seg.setAttribute('aria-label', label);
      seg.dataset.index = String(i);
      fragment.appendChild(seg);
      state.segments.push(seg);
    }
    state.progressEl.appendChild(fragment);
  }

  function attachSegmentClicks(state) {
    state.segments.forEach((seg) => {
      seg.addEventListener('click', () => navigateToSegment(state, Number(seg.dataset.index)));
    });
  }

  function navigateToSegment(state, segmentIdx) {
    let targetIdx;
    if (state.config.paginationMode === 'per-slide') {
      targetIdx = Math.min(state.slides.length - 1, Math.max(0, segmentIdx));
    } else {
      targetIdx = Math.min(state.lastIndex, segmentIdx * state.perPage);
    }
    const slide = state.slides[targetIdx];
    if (slide && typeof state.slider.scrollToElement === 'function') {
      state.slider.scrollToElement(slide);
    }
    // Always update the active-dot state, even when the target slide was
    // already visible and no scroll (and therefore no select event) fires.
    renderProgress(state, targetIdx);
    highlightSlide(state, slide);
  }

  function highlightSlide(state, slide) {
    const cfg = state.config.highlightOnClick;
    if (!cfg || !slide) return;
    const target = slide.querySelector(cfg.selector);
    if (!target) return;
    target.classList.remove(cfg.className);
    // Force reflow so the animation restarts on rapid successive clicks.
    void target.offsetWidth;
    target.classList.add(cfg.className);
  }

  function activeSegmentIndex(state, currentIndex) {
    if (state.config.paginationMode === 'per-slide') {
      return Math.min(state.slides.length - 1, Math.max(0, currentIndex));
    }
    if (state.totalPages <= 1) return 0;
    return Math.min(state.totalPages - 1, Math.floor(currentIndex / state.perPage));
  }

  function renderProgress(state, currentIndex) {
    state.currentIndex = currentIndex;
    const activeIdx = activeSegmentIndex(state, currentIndex);
    state.segments.forEach((seg, i) => {
      const active = i === activeIdx;
      seg.classList.toggle('is-active', active);
      if (active) seg.setAttribute('aria-current', 'true');
      else seg.removeAttribute('aria-current');
    });
    if (state.prevBtn) state.prevBtn.toggleAttribute('disabled', currentIndex <= 0);
    if (state.nextBtn) state.nextBtn.toggleAttribute('disabled', currentIndex >= state.lastIndex);
  }

  function refreshGeometry(state) {
    const slides = readSlides(state.slider);
    const perPage = readPerPage(state.slider);
    state.slides = slides;
    state.perPage = perPage;
    state.totalPages = Math.max(1, Math.ceil(slides.length / perPage));
    state.lastIndex = Math.max(0, slides.length - perPage);
    if (state.segments.length !== segmentCount(state)) {
      buildSegments(state);
      attachSegmentClicks(state);
    }
    updatePaginationVisibility(state);
    renderProgress(state, Math.min(state.currentIndex || 0, state.lastIndex));
  }

  function debounce(fn, wait) {
    let timer = null;
    return function (...args) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function readPerPage(slider) {
    const attr = parseInt(slider.dataset.perPage, 10);
    return attr > 0 ? attr : 1;
  }

  function readSlides(slider) {
    return Array.from(slider.querySelectorAll('.slider__item:not([hidden])'));
  }

  function createSectionState(root, config) {
    const slider = root.querySelector('carousel-slider');
    const progressEl = root.querySelector(config.progressSelector);
    if (!slider || !progressEl) return null;

    const slides = readSlides(slider);
    const perPage = readPerPage(slider);

    return {
      root,
      config,
      slider,
      progressEl,
      progressContainerEl: config.progressContainerSelector
        ? root.querySelector(config.progressContainerSelector)
        : null,
      prevBtn: root.querySelector(config.prevBtnSelector),
      nextBtn: root.querySelector(config.nextBtnSelector),
      slides,
      perPage,
      totalPages: Math.max(1, Math.ceil(slides.length / perPage)),
      lastIndex: Math.max(0, slides.length - perPage),
      segments: [],
      currentIndex: 0,
    };
  }

  function attachSliderListener(state) {
    state.slider.addEventListener('on:carousel-slider:select', (event) => {
      const idx = event.detail && typeof event.detail.index === 'number' ? event.detail.index : 0;
      renderProgress(state, idx);
    });
  }

  function attachResizeListener(state) {
    const onResize = debounce(() => refreshGeometry(state), 150);
    window.addEventListener('resize', onResize);
  }

  function scheduleInitialRender(state) {
    const render = () => {
      updatePaginationVisibility(state);
      renderProgress(state, 0);
    };
    if (document.readyState === 'complete') render();
    else window.addEventListener('load', render, { once: true });
  }

  function initSection(root, config) {
    if (root.dataset[INIT_FLAG] === 'true') return;
    root.dataset[INIT_FLAG] = 'true';

    const state = createSectionState(root, config);
    if (!state) return;

    wireSideArrows(state);
    buildSegments(state);
    attachSegmentClicks(state);
    updatePaginationVisibility(state);
    attachSliderListener(state);
    attachResizeListener(state);
    scheduleInitialRender(state);
  }

  function initAll(scope) {
    const root = scope || document;
    SECTION_CONFIGS.forEach((config) => {
      root.querySelectorAll(config.rootSelector).forEach((el) => initSection(el, config));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
